/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError } from 'axios';
import { Api } from '../axiosApi';

export type TPeriodoContas = 'mensal' | 'anual' | 'atual';
export type TAgrupamentoContas = 'dia' | 'mes';

export interface IGraficoContasItem {
  data: string;
  valorpago: number;
  qtdepago: number;
  valorrecebido: number;
  qtderecebido: number;
  saldorealizado: number;
  valorapagar: number;
  qtdeapagar: number;
  valorareceber: number;
  qtdeareceber: number;
  saldoprevisto: number;
}

export interface ITopClientePago {
  idparceiro: string;
  nome: string;
  valor: number;
  quantidade: number;
}

export interface IClienteAtrasado {
  idparceiro: string;
  nome: string;
  valor: number;
  quantidade: number;
  vencimentomaisantigo: string;
}

export interface IDRECategoriaItem {
  categoria: string;
  valor: number;
  quantidade: number;
}

export interface IDRE {
  receitabruta: number;
  qtdevendas: number;
  devolucoes: number;
  qtdedevolucoes: number;
  receitaliquida: number;
  despesas: IDRECategoriaItem[];
  despesastotal: number;
  resultado: number;
}

export interface IDashboardContasGraficos {
  intervalo: {
    datainicial: string;
    datafinal: string;
  };
  agrupamento: TAgrupamentoContas;
  totais: {
    valorpago: number;
    qtdepago: number;
    valorrecebido: number;
    qtderecebido: number;
    saldorealizado: number;
    valorapagar: number;
    qtdeapagar: number;
    valorareceber: number;
    qtdeareceber: number;
    saldoprevisto: number;
  };
  grafico: IGraficoContasItem[];
  topClientesPago: ITopClientePago[];
  clientesAtrasados: IClienteAtrasado[];
  dre: IDRE;
}

interface ErrorResponse {
  errors: {
    default: string;
  };
}

const getDashboardContasGraficos = async (
  periodo: TPeriodoContas = 'atual',
  ano?: number,
  mes?: number,
): Promise<IDashboardContasGraficos | Error> => {
  try {
    let urlRelativa = `/dashboardcontasgraficos?periodo=${periodo}`;

    if (periodo !== 'atual' && ano) urlRelativa += `&ano=${ano}`;
    if (periodo === 'mensal' && mes) urlRelativa += `&mes=${mes}`;

    const { data } = await Api.get(urlRelativa);

    if (data) {
      const converterItem = (item: any): IGraficoContasItem => ({
        data: item.data,
        valorpago: Number(item.valorpago || 0),
        qtdepago: Number(item.qtdepago || 0),
        valorrecebido: Number(item.valorrecebido || 0),
        qtderecebido: Number(item.qtderecebido || 0),
        saldorealizado: Number(item.saldorealizado || 0),
        valorapagar: Number(item.valorapagar || 0),
        qtdeapagar: Number(item.qtdeapagar || 0),
        valorareceber: Number(item.valorareceber || 0),
        qtdeareceber: Number(item.qtdeareceber || 0),
        saldoprevisto: Number(item.saldoprevisto || 0),
      });

      const converterTopCliente = (item: any): ITopClientePago => ({
        idparceiro: item.idparceiro,
        nome: item.nome,
        valor: Number(item.valor || 0),
        quantidade: Number(item.quantidade || 0),
      });

      const converterAtrasado = (item: any): IClienteAtrasado => ({
        idparceiro: item.idparceiro,
        nome: item.nome,
        valor: Number(item.valor || 0),
        quantidade: Number(item.quantidade || 0),
        vencimentomaisantigo: item.vencimentomaisantigo,
      });

      const converterDespesaCategoria = (item: any): IDRECategoriaItem => ({
        categoria: item.categoria,
        valor: Number(item.valor || 0),
        quantidade: Number(item.quantidade || 0),
      });

      return {
        intervalo: data.intervalo,
        agrupamento: data.agrupamento,
        totais: {
          valorpago: Number(data.totais?.valorpago || 0),
          qtdepago: Number(data.totais?.qtdepago || 0),
          valorrecebido: Number(data.totais?.valorrecebido || 0),
          qtderecebido: Number(data.totais?.qtderecebido || 0),
          saldorealizado: Number(data.totais?.saldorealizado || 0),
          valorapagar: Number(data.totais?.valorapagar || 0),
          qtdeapagar: Number(data.totais?.qtdeapagar || 0),
          valorareceber: Number(data.totais?.valorareceber || 0),
          qtdeareceber: Number(data.totais?.qtdeareceber || 0),
          saldoprevisto: Number(data.totais?.saldoprevisto || 0),
        },
        grafico: (data.grafico || []).map(converterItem),
        topClientesPago: (data.topClientesPago || []).map(converterTopCliente),
        clientesAtrasados: (data.clientesAtrasados || []).map(converterAtrasado),
        dre: {
          receitabruta: Number(data.dre?.receitabruta || 0),
          qtdevendas: Number(data.dre?.qtdevendas || 0),
          devolucoes: Number(data.dre?.devolucoes || 0),
          qtdedevolucoes: Number(data.dre?.qtdedevolucoes || 0),
          receitaliquida: Number(data.dre?.receitaliquida || 0),
          despesas: (data.dre?.despesas || []).map(converterDespesaCategoria),
          despesastotal: Number(data.dre?.despesastotal || 0),
          resultado: Number(data.dre?.resultado || 0),
        },
      };
    }

    return new Error('Erro ao listar os registros.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;

      if (axiosError.response) {
        const errorMessage =
          axiosError.response.data.errors?.default ||
          'Erro desconhecido';

        return new Error(errorMessage);
      }

      if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      }

      return new Error(axiosError.message);
    }

    return new Error('Erro inesperado');
  }
};

export const GraficosContasService = {
  getDashboardContasGraficos,
};
